import { useMutation } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { useRef } from "react";
import { getCoreAxiosInstance } from "../../api/CoreAxiosInstance";
import { CLIENT_PAY_NORMALISATION_API_PATHS } from "../config";
import type { NormalisationArtifactRequest } from "../types/normalisationRun";
import { toClientPayNormalisationErrorAsync } from "../utils/clientPayNormalisationApiError";
import { downloadBlob } from "../utils/downloadBlob";
import {
  normalisationArtifactDownloadFilename,
  normalisationArtifactFileName,
} from "../utils/normalisationArtifactPaths";

/**
 * Downloads normalisation run artifacts via GET blob (#1098).
 */
export function useClientPayNormalisationArtifacts() {
  const downloadAbortRef = useRef<AbortController | null>(null);

  const mutation = useMutation({
    mutationFn: async ({
      runId,
      kind,
      importFileName,
    }: NormalisationArtifactRequest) => {
      downloadAbortRef.current?.abort();
      const controller = new AbortController();
      downloadAbortRef.current = controller;

      const artifactFileName = normalisationArtifactFileName(kind);
      const instance = await getCoreAxiosInstance();
      try {
        const response = await instance.get(
          CLIENT_PAY_NORMALISATION_API_PATHS.artifact(runId, artifactFileName),
          { responseType: "blob", signal: controller.signal },
        );
        if (controller.signal.aborted) {
          return;
        }
        const blob = response.data as Blob;
        const mimeType =
          (typeof blob.type === "string" && blob.type !== ""
            ? blob.type
            : undefined) ?? "application/octet-stream";
        const filename = normalisationArtifactDownloadFilename(
          importFileName,
          kind,
        );
        downloadBlob(blob, mimeType, filename);
      } catch (error) {
        if (
          controller.signal.aborted ||
          (isAxiosError(error) && error.code === "ERR_CANCELED")
        ) {
          return;
        }
        throw await toClientPayNormalisationErrorAsync(error);
      } finally {
        if (downloadAbortRef.current === controller) {
          downloadAbortRef.current = null;
        }
      }
    },
  });

  const resetDownload = () => {
    downloadAbortRef.current?.abort();
    downloadAbortRef.current = null;
    mutation.reset();
  };

  return {
    downloadArtifact: mutation.mutate,
    isDownloading: mutation.isPending,
    downloadError: mutation.error,
    resetDownload,
  };
}
